import { Store } from 'redux';

import { IAppState } from '../../../../../shared/redux';
import { IOfferGeo } from '../../../../../shared/redux/reducers';
import { ECoworkingOfferType } from '../../../../../shared/repositories/coworking/v1/book-coworking';
import { getCoworkingSubTypes } from '../../../../../shared/utils/coworking/getCoworkingSubTypes';

import { IAddressService } from '../../../blocks/segments/address/addressService';
import { coworkingAmenitiesKeys } from '../../enums/coworking-amenities';
import { coworkingAnalyticSubTypes } from '../../enums/commercial-object-types';

import { IAddFormService } from '../addFormService';
import { ICoworkingService, TTrackCoworkingChangeSubType } from './types';
import { IAnalyticsService } from '../analyticsService';

export const coworkingService = (
  addFormService: IAddFormService,
  addressService: IAddressService,
  analyticsService: IAnalyticsService,
  store: Store<IAppState>,
): ICoworkingService => {
  'ngInject';
  addFormService.initCoworkingField();

  const { coworking } = store.getState();

  const svc: ICoworkingService = {
    isCoworkingOperator: coworking.isOperator || false,
    availableCoworkings: coworking.coworkingsByOperator || [],
    lkUrl: coworking.lkUrl,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  svc.userCanPublishCoworking = () => svc.isCoworkingOperator;

  svc.getCoworkingSuggests = () => svc.availableCoworkings.map(suggest => ({ ...suggest, suggestType: 'coworking' }));

  svc.isSingleCoworking = () => svc.availableCoworkings.length === 1;

  svc.clearCoworking = () => {
    addFormService.initCoworkingField();
  };

  svc.setCoworking = coworking => {
    const newCoworking = {
      ...coworking,
      amenities: coworking.amenities.filter(el => coworkingAmenitiesKeys.includes(el)),
    };

    addFormService.setValueByPath('coworking', newCoworking);

    if (addFormService.getFieldValue<IOfferGeo>('geo')) {
      addressService.setUserInput(newCoworking.address);
    }
  };

  svc.getAttachedCoworking = () => addFormService.getFieldValue('coworking');

  svc.isCoworkingAttached = () => !!svc.getAttachedCoworking()?.id;

  svc.isCoworkingCategory = () => addFormService.isCoworkingObject();

  svc.getCoworkingOfferType = () => addFormService.getFieldValue<ECoworkingOfferType>('coworkingOfferType');

  svc.setCoworkingOfferType = (coworkingOfferType: ECoworkingOfferType) =>
    addFormService.setValueByPath('coworkingOfferType', coworkingOfferType);

  svc.changeCoworkingOfferType = (coworkingOfferType: ECoworkingOfferType) => {
    const currentCoworkingOfferType = svc.getCoworkingOfferType();

    if (currentCoworkingOfferType !== coworkingOfferType) {
      svc.setCoworkingOfferType(coworkingOfferType);

      svc.clearCoworkingFields();
    }
  };

  svc.isFlexOffice = () => {
    const coworkingOfferType = svc.getCoworkingOfferType();

    return coworkingOfferType === ECoworkingOfferType.Office;
  };

  svc.isFlexibleWorkplace = () => {
    const coworkingOfferType = svc.getCoworkingOfferType();

    return coworkingOfferType === ECoworkingOfferType.FlexibleWorkplace;
  };

  svc.isFixedWorkplace = () => {
    const coworkingOfferType = svc.getCoworkingOfferType();

    return coworkingOfferType === ECoworkingOfferType.FixedWorkplace;
  };

  svc.isWorkplace = () => {
    return svc.isFixedWorkplace() || svc.isFlexibleWorkplace();
  };

  svc.getWorkplaceData = () => svc.getAttachedWorkplace() || {};

  svc.getAttachedWorkplace = () => {
    const coworking = svc.getAttachedCoworking();

    if (!coworking) {
      return null;
    }

    const coworkingOfferType = svc.getCoworkingOfferType();

    if (!coworkingOfferType || coworkingOfferType === ECoworkingOfferType.Office) {
      return null;
    }

    return coworking[coworkingOfferType];
  };

  svc.isCurrentWorkplaceAttached = () => {
    const attachedWorkplaceData = svc.getWorkplaceData();
    const offerId = addFormService.getFieldValue<number>('id');

    if (!offerId) {
      return false;
    }

    return !!attachedWorkplaceData.addformUrl?.includes(offerId.toString());
  };

  svc.canUserPublishOffer = () => {
    const isEditMode = addFormService.isEditMode();

    if (isEditMode) {
      return !svc.getAttachedWorkplace() || svc.isCurrentWorkplaceAttached();
    }

    if (!svc.isCoworkingAttached() || !svc.isWorkplace()) {
      return true;
    }

    return !svc.getAttachedWorkplace();
  };

  svc.clearCoworkingFields = () => {
    if (!svc.isFlexOffice()) {
      addFormService.setValueByPath('totalArea', undefined);
    }
  };

  svc.getLayoutSettings = () => ({
    isShortLayout: false,
    shortLayoutMode: 'coworking',
    isShortForm: svc.isSingleCoworking() || svc.isCoworkingAttached(),
  });

  svc.getCoworkingSegments = segments => {
    if (!svc.isCoworkingCategory()) {
      return segments.filter(segment => segment === 'ad-type');
    }

    if (!svc.isCoworkingAttached() || !svc.canUserPublishOffer()) {
      return segments.filter(segment => ['ad-type', 'address'].includes(segment));
    }

    return segments;
  };

  svc.attachSingleCoworking = () => {
    if (svc.isSingleCoworking()) {
      svc.setCoworking(svc.availableCoworkings[0]);
    }
  };

  const refreshCoworking = () => {
    if (!svc.isCoworkingAttached()) {
      svc.attachSingleCoworking();

      return;
    }

    const coworking = svc.getAttachedCoworking();

    if (!coworking?.id) {
      return;
    }

    const coworkingFromAvailableList = svc.availableCoworkings.find(el => el.id === coworking.id);

    if (!coworkingFromAvailableList) {
      return;
    }

    svc.setCoworking(coworkingFromAvailableList);
  };

  svc.initializeCoworkingObject = () => {
    refreshCoworking();
    addressService.setDefaultGeoObject();
    addFormService.setValueByPath('areaParts', undefined);
  };

  svc.trackCoworkingChangeSubType = (objectSubType: TTrackCoworkingChangeSubType) => {
    const coworkingAnalyticSubType = coworkingAnalyticSubTypes[objectSubType];

    if (coworkingAnalyticSubType) {
      analyticsService.trackChangeType('sub_objecttype_select', coworkingAnalyticSubType);
    }
  };

  svc.getCoworkingSubTypes = () => {
    return getCoworkingSubTypes();
  };

  svc.getLkUrl = () => svc.lkUrl;

  return svc;
};
